import { useNotification } from "@refinedev/core";
import { List } from "@refinedev/antd";
import { Button, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState, type FC } from "react";
import { axiosInstance } from "../../api/client";
import type { DeploymentTarget, DeployResult, SeoPostMetaItem, SeoRedirectItem } from "../../types";
import { SeoDeployModal } from "./_components/deploy-modal";
import { SeoDeployResultsModal } from "./_components/deploy-results-modal";
import { JsonViewerModal } from "./_components/json-viewer-modal";
import { SeoTargetsModal } from "./_components/targets-modal";
import { useSelectedProjectId } from "./_components/use-selected-project-id";
import type { SeoDeployResultRow, SeoKind } from "./types";
import { useSeoData } from "./use-seo-data";

dayjs.extend(relativeTime);

const { Text } = Typography;

// Which api path a given (kind, itemId) deploys through. Settings has no item id of its own
// (a project-wide singleton, see obsidian/Product/SEO Sync.md).
function basePath(projectId: string, kind: SeoKind, itemId: string | null): string {
  if (kind === "settings") return `/projects/${projectId}/seo/settings`;
  if (kind === "post-meta") return `/projects/${projectId}/seo/post-meta/${itemId}`;
  return `/projects/${projectId}/seo/redirects/${itemId}`;
}

function freshnessLabel(targets: DeploymentTarget[]): string {
  if (targets.length === 0) return "Never deployed";
  const upToDate = targets.filter((t) => t.upToDate).length;
  return `${upToDate}/${targets.length} up to date`;
}

interface DeployState {
  kind: SeoKind;
  itemId: string | null;
  title: string;
  targets: DeploymentTarget[];
}

interface TargetsState {
  kind: SeoKind;
  itemId: string | null;
  title: string;
}

export const SeoList: FC = () => {
  const { open: notify } = useNotification();
  const [selectedProjectId, setSelectedProjectId] = useSelectedProjectId();
  const { projects, settings, settingsTargets, postMeta, postMetaTargets, redirects, redirectTargets, loading, refetch } =
    useSeoData(selectedProjectId);

  const project = projects.find((p) => p.id === selectedProjectId) ?? null;

  const [deployState, setDeployState] = useState<DeployState | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [targetsState, setTargetsState] = useState<TargetsState | null>(null);
  const [deployResults, setDeployResults] = useState<SeoDeployResultRow[] | null>(null);
  const [jsonViewer, setJsonViewer] = useState<{ title: string; data: unknown } | null>(null);

  const environmentLabel = (environmentId: string): string => {
    const env = project?.environments.find((e) => e.id === environmentId);
    return env ? `${project?.name} / ${env.name}` : environmentId;
  };

  const confirmDeploy = async (environmentIds: string[]) => {
    if (!deployState || !selectedProjectId) return;
    setDeploying(true);

    const { data } = await axiosInstance.post<DeployResult[]>(`${basePath(selectedProjectId, deployState.kind, deployState.itemId)}/deploy`, {
      targetEnvironmentIds: environmentIds,
    });

    setDeploying(false);
    setDeployState(null);

    const results: SeoDeployResultRow[] = data.map((result) => ({ ...result, environmentLabel: environmentLabel(result.environmentId) }));
    if (results.length > 1 || results.some((r) => r.status === "failure")) {
      setDeployResults(results);
    } else {
      notify?.({ type: "success", message: "Deployed successfully" });
    }

    void refetch();
  };

  const removeTarget = async (kind: SeoKind, itemId: string | null, environmentId: string) => {
    if (!selectedProjectId) return;
    await axiosInstance.delete(`${basePath(selectedProjectId, kind, itemId)}/deployments/${environmentId}`);
    void refetch();
  };

  return (
    <List title="SEO" canCreate={false}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          style={{ minWidth: 260 }}
          placeholder="Select a project"
          value={selectedProjectId ?? undefined}
          onChange={setSelectedProjectId}
          options={projects.map((p) => ({ label: p.name, value: p.id }))}
        />
      </Space>

      {!selectedProjectId && <Text type="secondary">Select a project to see its published SEO data.</Text>}

      {selectedProjectId && (
        <Tabs
          items={[
            {
              key: "settings",
              label: "Settings",
              children: settings ? (
                <Space direction="vertical">
                  <Space>
                    <Text>{freshnessLabel(settingsTargets)}</Text>
                    <Text type="secondary">Last updated {dayjs(settings.updatedAt).fromNow()}</Text>
                  </Space>
                  <Space>
                    <Button size="small" onClick={() => setJsonViewer({ title: "SEO settings", data: settings.data })}>
                      View JSON
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => setDeployState({ kind: "settings", itemId: null, title: "Deploy settings to", targets: settingsTargets })}
                    >
                      Deploy
                    </Button>
                    {settingsTargets.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => setTargetsState({ kind: "settings", itemId: null, title: "Deployment targets — Settings" })}
                      >
                        Manage targets
                      </Button>
                    )}
                  </Space>
                </Space>
              ) : (
                <div style={{ padding: "32px 0" }}>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>No SEO settings published yet</p>
                  <p style={{ color: "#888", margin: 0 }}>
                    Run <Typography.Text code>lps seo publish</Typography.Text> from this project to share its SEO data here.
                  </p>
                </div>
              ),
            },
            {
              key: "post-meta",
              label: "Post meta",
              children: (
                <Table<SeoPostMetaItem>
                  dataSource={postMeta}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: "max-content" }}
                  pagination={postMeta.length > 10 ? { pageSize: 10 } : false}
                >
                  <Table.Column<SeoPostMetaItem> title="Post type" dataIndex="postType" />
                  <Table.Column<SeoPostMetaItem> title="Slug" dataIndex="slug" />
                  <Table.Column<SeoPostMetaItem>
                    title="Title"
                    render={(_, row) => (
                      <Space>
                        {row.title}
                        {row.deletedAt && <Tag color="orange">Removed from repo, still deployed on {(postMetaTargets[row.id] ?? []).length} site(s)</Tag>}
                      </Space>
                    )}
                  />
                  <Table.Column<SeoPostMetaItem> title="Status" render={(_, row) => <Text>{freshnessLabel(postMetaTargets[row.id] ?? [])}</Text>} />
                  <Table.Column<SeoPostMetaItem> title="Last updated" render={(_, row) => dayjs(row.updatedAt).fromNow()} />
                  <Table.Column<SeoPostMetaItem>
                    title=""
                    align="right"
                    render={(_, row) => (
                      <Space size="small">
                        <Button size="small" onClick={() => setJsonViewer({ title: `${row.postType} / ${row.slug}`, data: row.meta })}>
                          View JSON
                        </Button>
                        {!row.deletedAt && (
                          <Button
                            size="small"
                            onClick={() =>
                              setDeployState({
                                kind: "post-meta",
                                itemId: row.id,
                                title: `Deploy "${row.title}" to`,
                                targets: postMetaTargets[row.id] ?? [],
                              })
                            }
                          >
                            Deploy
                          </Button>
                        )}
                        {(postMetaTargets[row.id]?.length ?? 0) > 0 && (
                          <Button
                            size="small"
                            onClick={() => setTargetsState({ kind: "post-meta", itemId: row.id, title: `Deployment targets — ${row.title}` })}
                          >
                            Manage targets
                          </Button>
                        )}
                      </Space>
                    )}
                  />
                </Table>
              ),
            },
            {
              key: "redirects",
              label: "Redirects",
              children: (
                <Table<SeoRedirectItem>
                  dataSource={redirects}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: "max-content" }}
                  pagination={redirects.length > 10 ? { pageSize: 10 } : false}
                >
                  <Table.Column<SeoRedirectItem>
                    title="Redirect to"
                    render={(_, row) => (
                      <Space>
                        {row.urlTo}
                        {row.deletedAt && <Tag color="orange">Removed from repo, still deployed on {(redirectTargets[row.id] ?? []).length} site(s)</Tag>}
                      </Space>
                    )}
                  />
                  <Table.Column<SeoRedirectItem> title="Status" dataIndex="status" />
                  <Table.Column<SeoRedirectItem> title="Header code" dataIndex="headerCode" />
                  <Table.Column<SeoRedirectItem> title="Deploy status" render={(_, row) => <Text>{freshnessLabel(redirectTargets[row.id] ?? [])}</Text>} />
                  <Table.Column<SeoRedirectItem> title="Last updated" render={(_, row) => dayjs(row.updatedAt).fromNow()} />
                  <Table.Column<SeoRedirectItem>
                    title=""
                    align="right"
                    render={(_, row) => (
                      <Space size="small">
                        <Button size="small" onClick={() => setJsonViewer({ title: `Redirect to ${row.urlTo}`, data: row.sources })}>
                          View sources
                        </Button>
                        {!row.deletedAt && (
                          <Button
                            size="small"
                            onClick={() =>
                              setDeployState({
                                kind: "redirects",
                                itemId: row.id,
                                title: `Deploy redirect to "${row.urlTo}" to`,
                                targets: redirectTargets[row.id] ?? [],
                              })
                            }
                          >
                            Deploy
                          </Button>
                        )}
                        {(redirectTargets[row.id]?.length ?? 0) > 0 && (
                          <Button
                            size="small"
                            onClick={() => setTargetsState({ kind: "redirects", itemId: row.id, title: `Deployment targets — ${row.urlTo}` })}
                          >
                            Manage targets
                          </Button>
                        )}
                      </Space>
                    )}
                  />
                </Table>
              ),
            },
          ]}
        />
      )}

      <SeoDeployModal
        open={Boolean(deployState)}
        title={deployState?.title ?? ""}
        project={project}
        targets={deployState?.targets}
        deploying={deploying}
        onCancel={() => setDeployState(null)}
        onConfirm={(environmentIds) => void confirmDeploy(environmentIds)}
      />

      <SeoTargetsModal
        open={Boolean(targetsState)}
        title={targetsState?.title ?? ""}
        targets={
          targetsState?.kind === "settings"
            ? settingsTargets
            : targetsState?.kind === "post-meta"
              ? postMetaTargets[targetsState.itemId ?? ""] ?? []
              : targetsState?.kind === "redirects"
                ? redirectTargets[targetsState.itemId ?? ""] ?? []
                : []
        }
        onClose={() => setTargetsState(null)}
        onRemoveTarget={(environmentId) => {
          if (targetsState) void removeTarget(targetsState.kind, targetsState.itemId, environmentId);
        }}
      />

      <SeoDeployResultsModal results={deployResults} onClose={() => setDeployResults(null)} />

      <JsonViewerModal title={jsonViewer?.title ?? ""} data={jsonViewer?.data ?? null} onClose={() => setJsonViewer(null)} />
    </List>
  );
};
